import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  listenToRide, cancelRide, listenToRideDriverLocation, getRideDriverLocationOnce, listenToRideRequest, getRideFresh, getRideFromServerStrict,
  pollNearbyDrivers, submitRideRating, trackUrlForToken,
  ensureRideShare, listenToRideOtp, getCachedRideOtp, reportVehicleMatch, logRideEvent, listenToRiderNote,
  boostRideFare, keepWaitingRide,
} from '../services/firebase';
import { openWhatsappShare, buildTelUrl } from '../utils/support';
import { APP } from '../config/app';
import { useLanguage } from '../i18n/LanguageContext';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import CaptainHeaderCard from '../components/CaptainHeaderCard';
import MapRecenterIcon from '../components/MapRecenterIcon';
import TripFareDetails from '../components/TripFareDetails';
import RideProgress from '../components/RideProgress';
import CancelReasonModal from '../components/CancelReasonModal';
import { cancelStage } from '../utils/cancelFlow';
import RatingSheet from '../components/RatingSheet';
import ChatPanel from '../components/ChatPanel';
import SosSheet from '../components/SosSheet';
import DriverProfileModal from '../components/DriverProfileModal';
import RideLoader from '../components/RideLoader';
import WaitingClock from '../components/WaitingClock';
import { riderNoteSummary, riderNoteSeen } from '../utils/riderNote';
import {
  createMap, destroyMap, drawRoute, placeMarker, getDirections, fitMapToPoints, fitMapToPoint, clearMarkers,
  distanceBetweenM, placeNearbyAutoMarker, placeAssignedAutoMarker, getActiveRideMapPadding,
  AnimateGliderMarker, createPulsingRadar, NIGHT_MAP_STYLE, DAY_MAP_STYLE,
} from '../services/maps';

import { hapticSuccess, hapticSelection } from '../utils/haptics';
import { playAssignedChime } from '../utils/chime';
import { minimizeApp } from '../utils/androidBack';
import { isNativeApp } from '../utils/platform';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { formatDriverName } from '../utils/driverName';
import { updateLiveRideNotification, clearLiveRideNotification } from '../services/notifications';
import { queuePlayReviewForCompletedRide } from '../utils/playReview';
import { boostErrorKey } from '../utils/rideErrors';
import {
  projectOnRoute, remainingRoute, offRouteStep, canReroute, metresBetween, toPath,
  OFF_ROUTE_M, REDRAW_MIN_MOVE_M,
} from '../utils/liveRoute';

const PHASE_HEADLINE = {
  searching: 'active.findingCaptain',
  driver_assigned: 'active.captainOnWay',
  arrived: 'active.arrived',
  started: 'active.enjoyRide',
  completed: 'active.completed',
};

const ACTIVE_STATUSES = ['searching', 'driver_assigned', 'arrived', 'started'];
const SEARCH_TIMEOUT_MS = 90 * 1000;
const isTerminalRideStatus = (status) => status === 'completed' || status === 'cancelled';

// Mirrors rideSecurity.js's BOOST_TIERS on the server — keep both in sync.
// The server is the real authority (it resolves the tier itself from the
// ride's own nearestDriverKm), this copy only drives what the UI offers.
const BOOST_TIERS = [
  { maxKm: 1.5, amounts: [10, 15, 20], cap: 50 },
  { maxKm: 2.5, amounts: [20, 30, 40], cap: 60 },
  { maxKm: Infinity, amounts: [30, 45, 60], cap: 80 },
];
const resolveBoostTier = (nearestDriverKm) => {
  const km = Number(nearestDriverKm);
  if (!Number.isFinite(km) || km < 0) return BOOST_TIERS[0];
  return BOOST_TIERS.find((t) => km < t.maxKm) || BOOST_TIERS[BOOST_TIERS.length - 1];
};

/** Prefer Firestore as source of truth — RTDB is only a wake-up signal (never trust captain identity from RTDB). */
const mergeRide = (firestoreRide) => firestoreRide || null;

const STATUS_RANK = {
  searching: 1,
  driver_assigned: 2,
  arrived: 3,
  started: 4,
  completed: 5,
  cancelled: 5,
};

/** ms since epoch for a Firestore Timestamp | number | {seconds} | ISO string. */
const tsMillis = (u) => {
  if (!u) return 0;
  if (typeof u === 'number') return u;
  if (typeof u.toMillis === 'function') return u.toMillis();
  if (u.seconds != null) return u.seconds * 1000 + Math.floor((u.nanoseconds || 0) / 1e6);
  if (typeof u === 'string') { const t = Date.parse(u); return Number.isNaN(t) ? 0 : t; }
  return 0;
};

/**
 * The search clock belongs to the ride, not to this React screen.  A passenger
 * can leave the active-trip screen and return while matching continues, so use
 * the server-owned start of this search cycle.  `lastCancelledByDriverAt` is
 * the compatible anchor for rides created before `searchStartedAt` existed.
 */
const searchCycleStartedAtMs = (ride) => (
  tsMillis(ride?.searchStartedAt)
  || tsMillis(ride?.lastCancelledByDriverAt)
  || tsMillis(ride?.createdAt)
);

/** ms since epoch for a ride's updatedAt. */
const rideTsMillis = (r) => tsMillis(r?.updatedAt);

/** Fields that actually change what the trip screen renders — used to skip no-op re-renders. */
const rideSignature = (r) => (r
  ? [r.status, r.driverId, r.driverName, r.driverPhone, r.vehicleNumber, r.vehicleModel,
    r.driverPhoto, r.fare, r.rating, r.tip, rideTsMillis(r)].map((v) => v ?? '').join('|')
  : '');

/** Advance UI from RTDB wake-up when Firestore listener is stalled in WebView. */
const mergeRideWithRtdb = (firestoreRide, rtdbReq) => {
  const base = mergeRide(firestoreRide);
  if (!base) return null;
  const next = rtdbReq?.status;

  // RTDB is the only live channel when the Firestore listener stalls in the
  // WebView. It carries driverId (never driverName / vehicle — those stay
  // Firestore-only), so attach it to any active-status ride that is missing it.
  // This keeps the map marker, the live ETA and the "on the way" stamp alive
  // even when the full ride doc never arrives. Never for 'searching' (Case 1
  // strips identity on a rematch on purpose) and never for a driver we already
  // know was cancelled / reassigned.
  const rtdbDriverUsable = Boolean(
    rtdbReq?.driverId
    && rtdbReq.driverId !== base.reassignedFromDriverId
    && !(Array.isArray(base.cancelledDriverIds) && base.cancelledDriverIds.includes(rtdbReq.driverId)),
  );
  const withRtdbDriver = (r) => (
    rtdbDriverUsable && !r.driverId && ['driver_assigned', 'arrived', 'started'].includes(r.status)
      ? { ...r, driverId: rtdbReq.driverId }
      : r
  );

  if (!next || next === base.status) return withRtdbDriver(base);

  // Terminal states (completed / cancelled) must never roll back.
  if (['completed', 'cancelled'].includes(base.status)) return base;
  if (['completed', 'cancelled'].includes(next)) return { ...base, status: next };

  // Case 1: RTDB notifies of a driver cancellation (rematch to searching)
  if (next === 'searching' && ['driver_assigned', 'arrived'].includes(base.status)) {
    // Only roll back if Firestore is NOT already assigned to a new driver (e.g. Anna 2),
    // and only if RTDB is not older than Firestore's accepted/updated timestamp.
    const isNewDriverAssigned = Boolean(base.driverId)
      && base.driverId !== base.reassignedFromDriverId
      && !(Array.isArray(base.cancelledDriverIds) && base.cancelledDriverIds.includes(base.driverId));

    const rtdbTs = Number(rtdbReq?.updatedAt) || 0;
    // base.acceptedAt/updatedAt are already raw Firestore Timestamps here, not a
    // ride object — use tsMillis directly. rideTsMillis expects a ride-shaped
    // object and reads `.updatedAt` off it, so passing a Timestamp through it
    // always resolved to 0, making this staleness check permanently inert.
    const baseTs = tsMillis(base?.acceptedAt || base?.updatedAt);
    const isRtdbStale = Boolean(rtdbTs && baseTs && rtdbTs < baseTs);

    if (!rtdbReq.driverId && !isNewDriverAssigned && !isRtdbStale) {
      const patch = { ...base, status: 'searching' };
      delete patch.driverId;
      delete patch.driverName;
      delete patch.driverPhone;
      delete patch.driverPhoto;
      delete patch.vehicleNumber;
      delete patch.vehicleModel;
      delete patch.driverRating;
      delete patch.driverRatingAvg;
      delete patch.driverTotalRides;
      return patch;
    }
  }

  // Case 2: Firestore is already in 'searching' (rematch or initial)
  if (base.status === 'searching') {
    // Only advance to driver_assigned if RTDB has a genuine new driver assigned
    // that wasn't previously cancelled/reassigned.
    const isCancelledDriver = rtdbReq.driverId
      && (rtdbReq.driverId === base.reassignedFromDriverId
        || (Array.isArray(base.cancelledDriverIds) && base.cancelledDriverIds.includes(rtdbReq.driverId)));

    if (next === 'driver_assigned' && rtdbReq.driverId && !isCancelledDriver) {
      return { ...base, status: 'driver_assigned', driverId: rtdbReq.driverId };
    }
    // Otherwise, Firestore 'searching' takes precedence over stale RTDB driver_assigned.
    return withRtdbDriver(base);
  }

  // Case 3: Normal forward progression (searching -> driver_assigned -> arrived -> started -> completed)
  const from = STATUS_RANK[base.status] || 0;
  const to = STATUS_RANK[next] || 0;
  if (to <= from) return withRtdbDriver(base);
  const patch = { ...base, status: next };
  if (rtdbReq.driverId && !base.driverId) patch.driverId = rtdbReq.driverId;
  return withRtdbDriver(patch);
};

/**
 * Time-and-distance throttle: allow at most once every `everyMs`, or sooner
 * once the driver has moved more than `moveM`. Now used only for the
 * live-activity card push — the live trip ETA no longer calls Directions at
 * all (see the single ETA effect below).
 */
const shouldRecompute = (ref, pos, everyMs = 10000, moveM = 30) => {
  const now = Date.now();
  const prev = ref.current;
  if (prev && prev.at && now - prev.at < everyMs) {
    if (!pos || !prev.pos || distanceBetweenM(prev.pos, pos) < moveM) return false;
  }
  ref.current = { at: now, pos: pos ? { lat: pos.lat, lng: pos.lng } : null };
  return true;
};

/**
 * How old the server's live ETA (`rideLocations/{rideId}.etaAt`, refreshed
 * roughly every 20s by maybePushLiveTripProgress) may be before this screen
 * stops trusting it and falls back to local straight-line math. Generous
 * enough not to flap while an Anna is stopped at a junction, short enough
 * that a genuinely stalled relay can never leave a frozen ETA on screen.
 */
const ETA_SERVER_MAX_AGE_MS = 90 * 1000;

/** Local ETA when the server value is missing/stale: ~250 m per minute (15 km/h). */
const LOCAL_ETA_METRES_PER_MIN = 250;

const formatTimeStr = (ts) => {
  if (!ts) return '';
  let d = null;
  if (ts?.toDate && typeof ts.toDate === 'function') d = ts.toDate();
  else if (ts instanceof Date) d = ts;
  else if (typeof ts === 'number') d = new Date(ts);
  else if (ts?.seconds) d = new Date(ts.seconds * 1000);
  else if (typeof ts === 'string') d = new Date(ts);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
};

export default function ActiveRide() {
  const { rideId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user, userProfile } = useApp();
  const { isDark } = useTheme();
  const online = useNetworkStatus();
  const [ride, setRide] = useState(() => location.state?.initialRide || null);
  const [rtdbRequest, setRtdbRequest] = useState(null);
  const [driverLoc, setDriverLoc] = useState(null);
  const [nearbyDrivers, setNearbyDrivers] = useState([]);
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showSos, setShowSos] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [searchElapsedSec, setSearchElapsedSec] = useState(0);
  const [searchTimedOut, setSearchTimedOut] = useState(false);
  const [pickupEtaMin, setPickupEtaMin] = useState(null);
  const [dropEtaMin, setDropEtaMin] = useState(null);
  const [dismissedSearchActions, setDismissedSearchActions] = useState(false);
  const [rideMissing, setRideMissing] = useState(false);
  const [rideLoadTried, setRideLoadTried] = useState(false);
  const [tripOtp, setTripOtp] = useState('');
  // The rider's note for the Anna, if they left one (utils/riderNote.js).
  const [riderNote, setRiderNote] = useState(null);
  const [unexpectedStop, setUnexpectedStop] = useState(false);
  const [locationStale, setLocationStale] = useState(false);
  const [matchBusy, setMatchBusy] = useState(false);
  const [boostingAmt, setBoostingAmt] = useState(null);

  const lastMovedPos = useRef(null);
  const lastMovedTime = useRef(Date.now());
  const realtimeLocAtRef = useRef(0);
  // The server refresh is strictly a last-resort metadata backstop. Keep a
  // single request in flight so visibility, focus and push events cannot fan
  // out into duplicate reads for the same ride.
  const rideRefreshInFlightRef = useRef(false);
  const liveCardThrottle = useRef({ at: 0, pos: null, status: null });

  const [userPanned, setUserPanned] = useState(false);
  const userPannedRef = useRef(false);
  const initialFitDoneRef = useRef(false);
  const pickupMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  // The route line's current leg (status + target) and its live state: the full route, how far
  // along it the auto is, and the re-route budget (utils/liveRoute.js). Replaced at each new leg.
  const lastRouteLegRef = useRef(null);
  const liveLegRef = useRef(null);
  const routeDrawSeqRef = useRef(0);

  const mapRef = useRef(null);
  const sheetRef = useRef(null);
  const mapInstance = useRef(null);
  const markers = useRef([]);
  const nearbyMarkers = useRef(new Map());
  const routeLine = useRef(null);
  const routeRequestVersionRef = useRef(0);
  const gliderMarkerRef = useRef(null);
  const nearbyMarkerPositions = useRef(new Map());
  const [mapReady, setMapReady] = useState(false);
  const [sheetMinimized, setSheetMinimized] = useState(false);
  const navigateRef = useRef(navigate);
  const tRef = useRef(t);
  const rideRef = useRef(null);
  // Last ride state we actually committed to render — the ordering/no-op guard
  // in applyRide compares against this synchronously.
  const committedRideRef = useRef(location.state?.initialRide || null);
  const notifiedAcceptRef = useRef(false);
  const notifiedArrivingRef = useRef(false);
  const notifiedArrivedRef = useRef(false);
  const notifiedCompletedRef = useRef(false);
  // Wall-clock time we first saw each ride stage locally — fallback for the
  // stepper when the server stage timestamp (arrivedAt/startedAt/…) is absent
  // or hasn't resolved yet.
  const stageSeenAtRef = useRef({});
  const [showNightShield, setShowNightShield] = useState(false);
  const hasPromptedNightRef = useRef(false);

  const displayRide = useMemo(
    () => mergeRideWithRtdb(ride, rtdbRequest),
    [ride, rtdbRequest],
  );

  const driverProfile = useMemo(() => {
    if (!displayRide?.driverId) return null;
    return {
      id: displayRide.driverId,
      name: displayRide.driverName || null,
      phone: displayRide.driverPhone || null,
      photoUrl: displayRide.driverPhoto || null,
      vehicleNumber: displayRide.vehicleNumber || null,
      vehicleModel: displayRide.vehicleModel || null,
      ratingAvg: displayRide.driverRatingAvg ?? displayRide.driverRating ?? null,
      totalRides: displayRide.driverTotalRides ?? displayRide.totalCompletedRides ?? displayRide.lifetimeRides ?? displayRide.driverCompletedRides ?? null,
    };
  }, [
    displayRide?.driverId,
    displayRide?.driverName,
    displayRide?.driverPhone,
    displayRide?.driverPhoto,
    displayRide?.vehicleNumber,
    displayRide?.vehicleModel,
    displayRide?.driverRatingAvg,
    displayRide?.driverRating,
    displayRide?.driverTotalRides,
    displayRide?.totalCompletedRides,
    displayRide?.lifetimeRides,
    displayRide?.driverCompletedRides,
  ]);

  const applyRide = useCallback((data) => {
    setRideLoadTried(true);
    if (!data) {
      setRideMissing(true);
      return;
    }
    if (user?.uid && data.customerId && data.customerId !== user.uid) {
      setRideMissing(true);
      return;
    }

    // Out-of-order / no-op guard. The trip screen pulls the ride from several
    // channels at once (Firestore listener, RTDB request, a poll, push-triggered
    // refetches). A slow getDocFromServer that resolves late can deliver a stale
    // 'searching' *after* the driver already arrived — which blanked the driver
    // card + OTP and fired a bogus "had to cancel" toast. Drop anything strictly
    // older than what we've already shown; skip re-render when nothing changed.
    const prevRide = committedRideRef.current;
    if (prevRide && prevRide.id === data.id) {
      // Terminal lock. A completed/cancelled ride never legitimately steps
      // back — but a lagging RTDB mirror or a late push-triggered refetch can
      // still deliver a stale 'driver_assigned'. Drop it so it can't blank the
      // summary card or re-fire an earlier stage toast.
      const prevTerminal = prevRide.status === 'completed' || prevRide.status === 'cancelled';
      const nextTerminal = data.status === 'completed' || data.status === 'cancelled';
      if (prevTerminal && !nextTerminal) return;

      // Legitimate server-authoritative rematch check:
      // Protects both directions:
      // 1. Assigned -> Searching (Driver cancellation)
      // 2. Searching -> Driver Assigned (New driver acceptance on rematch)
      const isRematchCancellation = data.status === 'searching' && (
        prevRide.status === 'driver_assigned'
        || prevRide.status === 'arrived'
        || Boolean(data.reassignedFromDriverId && prevRide.status !== 'searching')
        || Boolean(data.lastCancelledByDriverAt)
      );

      const isRematchAcceptance = prevRide.status === 'searching' && (
        ['driver_assigned', 'arrived'].includes(data.status)
        && Boolean(data.driverId)
        && data.driverId !== prevRide.reassignedFromDriverId
      );

      const isServerRematch = isRematchCancellation || isRematchAcceptance;

      if (!isServerRematch) {
        const prevTs = rideTsMillis(prevRide);
        const nextTs = rideTsMillis(data);
        if (prevTs && nextTs && nextTs < prevTs) return;
        if (rideSignature(prevRide) === rideSignature(data)) {
          committedRideRef.current = data;
          return;
        }
      }
    }
    committedRideRef.current = data;
    if (data.status && !stageSeenAtRef.current[data.status]) {
      stageSeenAtRef.current[data.status] = Date.now();
    }

    setRideMissing(false);
    setRide(data);

    // Synchronously sync local rtdbRequest state so any lingering 'searching' packet is evicted
    if (['driver_assigned', 'arrived', 'started'].includes(data.status) && data.driverId) {
      setRtdbRequest((curReq) => {
        if (!curReq || curReq.status === 'searching' || curReq.driverId !== data.driverId) {
          return { ...(curReq || {}), status: data.status, driverId: data.driverId, updatedAt: Date.now() };
        }
        return curReq;
      });
    }

    // Auto-Rematch: Driver had to cancel -> Trip returned to 'searching' to find next Anna
    const wasAssigned = prevRide?.status === 'driver_assigned' || prevRide?.status === 'arrived'
      || notifiedAcceptRef.current || notifiedArrivedRef.current;
    if (data.status === 'searching' && (wasAssigned || data.reassignedFromDriverId)) {
      notifiedAcceptRef.current = false;
      notifiedArrivedRef.current = false;
      notifiedArrivingRef.current = false;
      setPickupEtaMin(null);
      setDropEtaMin(null);
      setDriverLoc(null);
      if (gliderMarkerRef.current) {
        try { gliderMarkerRef.current.destroy(); } catch { /* */ }
        gliderMarkerRef.current = null;
      }
      delete stageSeenAtRef.current['driver_assigned'];
      delete stageSeenAtRef.current['arrived'];
      clearLiveRideNotification();

      // Clear any stale driverId from RTDB request in local state
      setRtdbRequest((curReq) => (curReq && curReq.status !== 'searching' ? { ...curReq, status: 'searching', driverId: null } : curReq));

      const driverName = data.lastCancelledDriverName || 'Anna';
      toast(tRef.current('active.annaCancelledSearching', { name: driverName }) || `${driverName} had to cancel. Finding another Anna for you...`, {
        icon: '⚠️',
        duration: 6000,
        id: 'driver-cancelled-reassign',
      });
    }

    if (data.status === 'driver_assigned' && !notifiedAcceptRef.current) {
      notifiedAcceptRef.current = true;
      hapticSuccess();
      playAssignedChime();
      toast.success(tRef.current('active.captainAssigned'), { id: 'status-assigned' });
    }
    if (data.status === 'arrived' && !notifiedArrivedRef.current) {
      notifiedArrivedRef.current = true;
      hapticSuccess();
      toast.success(tRef.current('active.arrived'), { id: 'status-arrived', duration: 5000 });
    }
    if (data.status === 'completed') {
      hapticSuccess();
      clearLiveRideNotification();
      if (!data.rating) {
        setShowChat(false);
        setShowCancel(false);
        setShowSos(false);
        setShowRating(true);
      } else if (!notifiedCompletedRef.current) {
        notifiedCompletedRef.current = true;
        toast.success(tRef.current('active.completed'), { id: 'status-completed' });
        setTimeout(() => navigateRef.current('/rides', { replace: true }), 2000);
      }
    }
    if (data.status === 'cancelled') {
      clearLiveRideNotification();
      if (data.autoExpired) {
        toast(tRef.current('active.searchExpiredToast'), { id: 'search-expired', icon: '🛺' });
      }
      navigateRef.current('/', { replace: true });
    }
  }, [user?.uid]);

  // Level-triggered backstop for the rating card. `displayRide` (what the UI
  // actually renders) can show 'completed' from the fast RTDB channel before
  // Firestore's own listener delivers that same transition to applyRide above
  // — RTDB never carries a `rating` field, so displayRide.rating always
  // reflects Firestore's real value regardless of which channel moved status
  // first. Without this, a passenger could land on the completed screen with
  // the one-shot trigger inside applyRide never firing for it.
  useEffect(() => {
    if (displayRide?.status === 'completed' && !displayRide.rating && !showRating) {
      setShowChat(false);
      setShowCancel(false);
      setShowSos(false);
      setShowRating(true);
    }
  }, [displayRide?.status, displayRide?.rating, showRating]);

  useEffect(() => {
    if (!displayRide || !['driver_assigned', 'arrived', 'started'].includes(displayRide.status)) return;
    const hour = new Date().getHours();
    const isNight = hour >= 20 || hour < 6;
    if (isNight && !hasPromptedNightRef.current) {
      hasPromptedNightRef.current = true;
      setShowNightShield(true);
    }
  }, [displayRide?.status]);

  useEffect(() => {
    return () => {
      // A Directions response can arrive after this screen has unmounted.
      // Invalidate it so it cannot attach an overlay to a retired map.
      routeRequestVersionRef.current += 1;
      if (gliderMarkerRef.current) {
        gliderMarkerRef.current.destroy();
        gliderMarkerRef.current = null;
      }
    };
  }, []);

  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => { tRef.current = t; }, [t]);
  useEffect(() => { rideRef.current = ride; }, [ride]);

  // Opened from a chat push notification (?chat=1) — jump straight to the chat.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('chat') === '1') {
      setShowChat(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!rideId) return undefined;
    return listenToRide(rideId, applyRide, (err) => {
      console.error('listenToRide failed:', err?.code);
      getRideFresh(rideId).then(applyRide).catch(() => {});
    });
  }, [rideId, applyRide]);

  useEffect(() => {
    if (!rideId) return undefined;
    return listenToRideRequest(rideId, (req) => {
      setRtdbRequest(req);
      const cur = rideRef.current?.status;
      const isRematch = req?.status === 'searching'
        && !req.driverId
        && ['driver_assigned', 'arrived'].includes(cur);
      const advancing = req?.status && cur
        && (STATUS_RANK[req.status] || 0) > (STATUS_RANK[cur] || 0);

      if (isRematch) {
        // 0ms instant optimistic status transition to searching on rematch
        setRide((prev) => {
          if (!prev) return prev;
          const copy = { ...prev, status: 'searching' };
          delete copy.driverId;
          delete copy.driverName;
          delete copy.driverPhone;
          delete copy.driverPhoto;
          delete copy.vehicleNumber;
          delete copy.vehicleModel;
          delete copy.driverRating;
          delete copy.driverRatingAvg;
          delete copy.driverTotalRides;
          return copy;
        });
        setPickupEtaMin(null);
        setDropEtaMin(null);
        setDriverLoc(null);
        if (gliderMarkerRef.current) {
          try { gliderMarkerRef.current.destroy(); } catch { /* */ }
          gliderMarkerRef.current = null;
        }
        delete stageSeenAtRef.current['driver_assigned'];
        delete stageSeenAtRef.current['arrived'];
        notifiedAcceptRef.current = false;
        notifiedArrivedRef.current = false;
        notifiedArrivingRef.current = false;
      } else if (advancing) {
        // 0ms instant optimistic status transition
        setRide((prev) => (prev ? { ...prev, status: req.status } : prev));
        if (req.status === 'arrived' && !notifiedArrivedRef.current) {
          notifiedArrivedRef.current = true;
          hapticSuccess();
          toast.success(tRef.current('active.arrived') || 'Anna has arrived at pickup!', { id: 'status-arrived', duration: 5000 });
        }
        if (req.status === 'started') {
          hapticSuccess();
          toast.success(tRef.current('active.tripStartedToast') || 'Trip started! Heading to destination.', { id: 'status-started' });
        }
      }
      if (req?.status && req.status !== cur) {
        getRideFresh(rideId).then(applyRide).catch(() => {});
      }
    });
  }, [rideId, applyRide]);

  // Firestore and RTDB are the primary ride-state channels. Push, foreground
  // and reconnect events each force an immediate recovery read. The interval
  // below is only a low-frequency fallback for a silently stalled WebView
  // listener (for example, a late driver photo or fare update), never the
  // mechanism that keeps normal ride status current.
  useEffect(() => {
    if (!rideId || isTerminalRideStatus(displayRide?.status)) return undefined;
    let disposed = false;
    const isVisible = () => document.visibilityState === 'visible';
    const isTerminal = () => (
      isTerminalRideStatus(committedRideRef.current?.status)
      || isTerminalRideStatus(rideRef.current?.status)
    );

    const refresh = async () => {
      // No background reads, no reads after the trip has ended, and no
      // overlapping reads when Android dispatches both focus and visibility.
      if (disposed || !isVisible() || isTerminal() || rideRefreshInFlightRef.current) return;
      rideRefreshInFlightRef.current = true;
      try {
        const freshRide = await getRideFresh(rideId);
        // A terminal event can arrive while the fallback request is in flight.
        // Do not allow that late response to re-open a completed/cancelled trip.
        if (!disposed && !isTerminal()) applyRide(freshRide);
      } catch {
        // Realtime/push remain active; the next recovery opportunity retries.
      } finally {
        rideRefreshInFlightRef.current = false;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onPush = (e) => {
      const data = e?.detail?.data || e?.detail || {};
      if (!data.rideId || data.rideId === rideId) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('mt_passenger_push', onPush);
    // Do not refresh on mount: Firestore's initial snapshot owns that first
    // load. This is a fallback only, and 20 seconds is enough for cosmetic
    // metadata while preserving recovery if a native WebView listener stalls.
    const poll = setInterval(refresh, 20000);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('mt_passenger_push', onPush);
      clearInterval(poll);
    };
  }, [rideId, applyRide, displayRide?.status]);

  useEffect(() => {
    if (!rideId) return undefined;
    const cached = getCachedRideOtp(rideId);
    if (cached) setTripOtp(cached);
    return listenToRideOtp(rideId, (otp) => {
      if (otp) setTripOtp(otp);
    });
  }, [rideId]);

  useEffect(() => listenToRiderNote(rideId, setRiderNote), [rideId]);

  useEffect(() => {
    if (displayRide?.status !== 'started' || !driverLoc?.lat || !driverLoc?.lng) {
      setUnexpectedStop(false);
      return;
    }
    const now = Date.now();
    if (!lastMovedPos.current) {
      lastMovedPos.current = { lat: driverLoc.lat, lng: driverLoc.lng };
      lastMovedTime.current = now;
      return;
    }
    const distMoved = distanceBetweenM(lastMovedPos.current, driverLoc);
    if (distMoved > 50) {
      lastMovedPos.current = { lat: driverLoc.lat, lng: driverLoc.lng };
      lastMovedTime.current = now;
      setUnexpectedStop(false);
    } else if (now - lastMovedTime.current > 5 * 60 * 1000) {
      setUnexpectedStop(true);
    }
  }, [displayRide?.status, driverLoc]);

  const clearNearbyMarkers = useCallback(() => {
    nearbyMarkers.current.forEach((m) => {
      try { m.setMap(null); } catch { /* */ }
    });
    nearbyMarkers.current.clear();
    nearbyMarkerPositions.current.clear();
  }, []);

  const getSheetHeight = useCallback(() => {
    if (sheetMinimized) return 56;
    return sheetRef.current?.getBoundingClientRect().height || 280;
  }, [sheetMinimized]);

  const getNearbyAutosToRender = useCallback((drivers) => {
    if (!drivers || !drivers.length) return [];
    return drivers.map((d) => ({ id: d.id, lat: d.lat, lng: d.lng }));
  }, []);

  const syncNearbyMarkers = useCallback((drivers) => {
    const map = mapInstance.current;
    const pickup = displayRide?.pickup;
    if (!map || !pickup || displayRide?.status !== 'searching') {
      clearNearbyMarkers();
      return;
    }
    const targetList = getNearbyAutosToRender(drivers);
    const ids = new Set(targetList.map((d) => d.id));

    nearbyMarkers.current.forEach((marker, id) => {
      if (!ids.has(id)) {
        try { marker.setMap(null); } catch { /* */ }
        nearbyMarkers.current.delete(id);
        nearbyMarkerPositions.current.delete(id);
      }
    });

    targetList.forEach((d) => {
      const pos = { lat: d.lat, lng: d.lng };
      const existing = nearbyMarkers.current.get(d.id);
      if (existing) {
        const previous = nearbyMarkerPositions.current.get(d.id);
        // Nearby dots are intentionally rounded to roughly 110m by the
        // server. Do not ask Maps to repaint a marker when the rounded result
        // has not meaningfully moved.
        if (!previous || distanceBetweenM(previous, pos) >= 12) {
          existing.setPosition(pos);
          nearbyMarkerPositions.current.set(d.id, pos);
        }
        return;
      }
      const marker = placeNearbyAutoMarker(map, pos);
      if (marker) {
        if (displayRide?.status !== 'searching') {
          try { marker.setMap(null); } catch { /* */ }
        } else {
          nearbyMarkers.current.set(d.id, marker);
          nearbyMarkerPositions.current.set(d.id, pos);
        }
      }
    });
  }, [displayRide?.pickup?.lat, displayRide?.pickup?.lng, displayRide?.status, clearNearbyMarkers, getNearbyAutosToRender]);

  const pickupLat = displayRide?.pickup?.lat;
  const pickupLng = displayRide?.pickup?.lng;
  const rideStatus = displayRide?.status;
  const assignedDriverId = displayRide?.driverId;

  useEffect(() => {
    if (rideStatus !== 'searching' || !pickupLat || !pickupLng) {
      setNearbyDrivers([]);
      clearNearbyMarkers();
      return undefined;
    }
    return pollNearbyDrivers(
      { lat: pickupLat, lng: pickupLng },
      (list) => {
        const mapped = (list || []).map((d) => ({
          id: d.id,
          lat: d.lat,
          lng: d.lng,
          distanceKm: d.distanceKm,
          distanceM: (d.distanceKm || 0) * 1000,
          updatedAt: d.updatedAt,
        })).filter((d) => d.id !== assignedDriverId);
        setNearbyDrivers((previous) => {
          const unchanged = previous.length === mapped.length
            && previous.every((d, index) => (
              d.id === mapped[index].id
              && d.lat === mapped[index].lat
              && d.lng === mapped[index].lng
              && d.distanceKm === mapped[index].distanceKm
            ));
          return unchanged ? previous : mapped;
        });
      },
      (err) => {
        // Handled silently
      },
      // These markers are cosmetic; dispatch itself is server-side and does
      // not depend on this poll. A 10s initial cadence keeps them responsive
      // while cutting repeated fleet reads roughly in half.
      { radiusKm: APP.nearbyCaptainRadiusKm, intervalMs: 10000, slowIntervalMs: 15000 },
    );
  }, [rideStatus, pickupLat, pickupLng, assignedDriverId, clearNearbyMarkers]);

  const radarRef = useRef(null);

  useEffect(() => {
    const pickup = displayRide?.pickup;
    if (displayRide?.status === 'searching' && mapInstance.current && pickup?.lat) {
      if (!radarRef.current) {
        radarRef.current = createPulsingRadar(mapInstance.current, pickup);
      }
    } else if (radarRef.current) {
      radarRef.current.cleanup?.();
      radarRef.current = null;
    }
    return () => {
      if (radarRef.current) {
        radarRef.current.cleanup?.();
        radarRef.current = null;
      }
    };
  }, [displayRide?.status, displayRide?.pickup?.lat, displayRide?.pickup?.lng, mapReady]);

  useEffect(() => {
    if (displayRide?.status !== 'searching') {
      clearNearbyMarkers();
      return;
    }
    if (!mapReady) return;
    syncNearbyMarkers(nearbyDrivers);
  }, [displayRide?.status, nearbyDrivers, syncNearbyMarkers, clearNearbyMarkers, mapReady]);

  useEffect(() => {
    if (!pickupLat || !pickupLng || mapReady) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (!mapRef.current) return;
        const map = await createMap(mapRef.current, { lat: pickupLat, lng: pickupLng }, { allowNative: true });
        if (cancelled) { destroyMap(map); return; }
        mapInstance.current = map;

        // Track when user manually interacts with the map
        map.addListener('dragstart', () => {
          userPannedRef.current = true;
          setUserPanned(true);
        });

        setMapReady(true);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [pickupLat, pickupLng, mapReady]);

  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.setOptions({
      styles: DAY_MAP_STYLE,
    });
  }, []);

  // A native map lives outside the page (under the WebView), so it must be
  // released when this screen closes (its markers go with it). No-op for the
  // JS map. Declared after the radar effect so the radar's cleanup runs first.
  useEffect(() => () => {
    destroyMap(mapInstance.current);
    mapInstance.current = null;
  }, []);

  const fitAllOnMap = useCallback((force = false) => {
    if (!mapInstance.current || !displayRide) return;
    if (userPannedRef.current && !force) return;
    const map = mapInstance.current;
    const pad = getActiveRideMapPadding({ sheetHeight: getSheetHeight() });

    // PHASE 1: Searching -> Center & Zoom in on Pickup (Pin A)
    if (displayRide.status === 'searching') {
      if (displayRide.pickup?.lat) {
        fitMapToPoint(map, displayRide.pickup, pad, 15.8);
      }
      return;
    }

    // PHASE 2: Anna Assigned / Arrived -> Fit bounds between Anna & Pickup (Pin A)
    if (['driver_assigned', 'arrived'].includes(displayRide.status)) {
      const points = [];
      if (displayRide.pickup) points.push(displayRide.pickup);
      if (driverLoc?.lat) {
        points.push({ lat: driverLoc.lat, lng: driverLoc.lng });
      } else if (displayRide.destination?.lat) {
        points.push({ lat: displayRide.destination.lat, lng: displayRide.destination.lng });
      }
      if (points.length) {
        fitMapToPoints(map, points, pad, 16.0);
      }
      return;
    }

    // PHASE 3: On Trip -> Fit bounds between Auto & Destination (Pin B)
    if (displayRide.status === 'started') {
      const points = [];
      if (driverLoc?.lat) points.push({ lat: driverLoc.lat, lng: driverLoc.lng });
      else if (displayRide.pickup) points.push(displayRide.pickup);
      if (displayRide.destination?.lat) {
        points.push({ lat: displayRide.destination.lat, lng: displayRide.destination.lng });
      }
      if (points.length) {
        fitMapToPoints(map, points, pad, 16.0);
      }
      return;
    }

    // Default fallback
    const points = [];
    if (displayRide.pickup) points.push(displayRide.pickup);
    if (displayRide.destination?.lat) {
      points.push({ lat: displayRide.destination.lat, lng: displayRide.destination.lng });
    }
    if (points.length) fitMapToPoints(map, points, pad, 16.0);
  }, [displayRide, driverLoc, getSheetHeight]);

  // Shows `points` as the route line (fewer than 2 clears it). The old line goes only once the new
  // one exists, so it never flickers; only the newest draw of the current leg is kept.
  const showRouteLine = useCallback((map, points, version) => {
    const seq = ++routeDrawSeqRef.current;
    if (!points || points.length < 2) {
      if (routeLine.current) {
        routeLine.current.setMap(null);
        routeLine.current = null;
      }
      return;
    }
    drawRoute(map, points).then((line) => {
      if (seq !== routeDrawSeqRef.current || version !== routeRequestVersionRef.current
        || mapInstance.current !== map) {
        line?.setMap(null);
        return;
      }
      if (routeLine.current) routeLine.current.setMap(null);
      routeLine.current = line;
    }).catch(() => {});
  }, []);

  // Draws what is still ahead of the auto: the line starts under him and the driven part is gone.
  // Off the route it leaves the line alone (a re-route is coming), unless `force` (a fresh route).
  const drawLegFromAuto = useCallback((map, leg, pos, force = false) => {
    if (!leg.follow || !pos) {
      showRouteLine(map, leg.path, leg.version);
      return;
    }
    const projection = projectOnRoute(leg.path, pos, leg.progress);
    if (!projection || projection.offM > OFF_ROUTE_M) {
      if (force) showRouteLine(map, leg.path, leg.version);
      return;
    }
    leg.progress = { segment: projection.segment, t: projection.t };
    if (!force && leg.drawnFrom && metresBetween(leg.drawnFrom, projection.point) < REDRAW_MIN_MOVE_M) return;
    leg.drawnFrom = projection.point;
    showRouteLine(map, remainingRoute(leg.path, projection), leg.version);
  }, [showRouteLine]);

  // One Directions call for the leg, from `origin` (the leg's start, or the auto when re-routing).
  const fetchLegRoute = useCallback((map, leg, origin) => {
    leg.fetching = true;
    getDirections(origin, leg.target).then((info) => {
      if (liveLegRef.current !== leg || leg.version !== routeRequestVersionRef.current
        || mapInstance.current !== map) return;
      leg.path = toPath(info.polyline);
      leg.progress = null;
      leg.offFixes = 0;
      leg.drawnFrom = null;
      // From where he is now: he has usually moved while the route was fetched.
      drawLegFromAuto(map, leg, leg.lastPos, true);
    }).catch(() => {
      // Retried from the auto's next GPS update, after the cooldown (followLeg).
      leg.lastRerouteAt = Date.now();
    }).finally(() => {
      leg.fetching = false;
    });
  }, [drawLegFromAuto]);

  // Every GPS update of a leg that follows the auto: trim the line (free), or fetch a new route
  // when he has really left it (paid, so limited: utils/liveRoute.js).
  const followLeg = useCallback((map, leg, pos, now) => {
    leg.lastPos = pos;
    if (leg.fetching) return;
    if (!leg.path) {
      if (canReroute(leg, now)) {
        leg.reroutes += 1;
        leg.lastRerouteAt = now;
        fetchLegRoute(map, leg, pos);
      }
      return;
    }
    const projection = projectOnRoute(leg.path, pos, leg.progress);
    if (!projection) return;
    const step = offRouteStep(leg, projection.offM, now);
    leg.offFixes = step.offFixes;
    if (step.reroute) {
      leg.reroutes += 1;
      leg.lastRerouteAt = now;
      fetchLegRoute(map, leg, pos);
      return;
    }
    drawLegFromAuto(map, leg, pos);
  }, [fetchLegRoute, drawLegFromAuto]);

  // Marker & Route Overlays
  useEffect(() => {
    if (!mapInstance.current || !mapReady || !displayRide) return;
    const map = mapInstance.current;
    const isSearching = displayRide.status === 'searching';
    const driverPosition = !isSearching && driverLoc
      && Number.isFinite(Number(driverLoc.lat)) && Number.isFinite(Number(driverLoc.lng))
      ? { lat: Number(driverLoc.lat), lng: Number(driverLoc.lng) }
      : null;

    if (displayRide.pickup && !pickupMarkerRef.current) {
      placeMarker(map, displayRide.pickup, 'A').then((m) => {
        pickupMarkerRef.current = m;
        markers.current.push(m);
      }).catch(() => {});
    }

    if (displayRide.destination?.lat && !destMarkerRef.current) {
      placeMarker(map, { lat: displayRide.destination.lat, lng: displayRide.destination.lng }, 'B').then((m) => {
        destMarkerRef.current = m;
        markers.current.push(m);
      }).catch(() => {});
    }

    if (driverPosition) {
      if (!gliderMarkerRef.current) {
        gliderMarkerRef.current = new AnimateGliderMarker(map, driverPosition);
      } else {
        gliderMarkerRef.current.updatePosition(driverPosition);
      }
    } else if (gliderMarkerRef.current) {
      gliderMarkerRef.current.destroy();
      gliderMarkerRef.current = null;
    }

    // The route line, one leg at a time: searching = pickup -> drop preview; driver_assigned =
    // Anna -> pickup; started = auto -> drop; arrived = no line (he is at the pickup). A new leg
    // starts on a status change (or a moved pickup/drop) and costs one Directions call; during a
    // leg the line follows the auto (followLeg). The legs that start at the Anna wait for his
    // position: his first GPS ping lands 2-5 s after the status flips, and starting early would
    // draw the wrong line for the whole leg.
    const routeStatus = displayRide.status;
    const legNeedsDriverPos = routeStatus === 'driver_assigned' || routeStatus === 'started';
    if (!(legNeedsDriverPos && !driverPosition)) {
      const dest = displayRide.destination?.lat
        ? { lat: displayRide.destination.lat, lng: displayRide.destination.lng }
        : null;
      const target = routeStatus === 'driver_assigned' ? displayRide.pickup : dest;
      const origin = legNeedsDriverPos ? driverPosition : displayRide.pickup;
      const legKey = routeStatus === 'arrived'
        ? routeStatus
        : `${routeStatus}|${target ? `${target.lat},${target.lng}` : ''}`;
      if (lastRouteLegRef.current !== legKey) {
        lastRouteLegRef.current = legKey;
        const version = ++routeRequestVersionRef.current;
        liveLegRef.current = null;
        // The previous leg's line goes now: no line beats a wrong one if this leg's route fails.
        showRouteLine(map, null, version);
        if (routeStatus !== 'arrived' && origin && target) {
          const leg = {
            version,
            target,
            follow: legNeedsDriverPos,
            path: null,
            progress: null,
            drawnFrom: null,
            lastPos: driverPosition,
            offFixes: 0,
            reroutes: 0,
            lastRerouteAt: 0,
            fetching: false,
          };
          liveLegRef.current = leg;
          fetchLegRoute(map, leg, origin);
        }
      } else if (driverPosition && liveLegRef.current?.follow) {
        followLeg(map, liveLegRef.current, driverPosition, Date.now());
      }
    }

    if (displayRide.status !== 'searching') {
      clearNearbyMarkers();
    }
  }, [
    displayRide?.status,
    displayRide?.pickup?.lat,
    displayRide?.pickup?.lng,
    displayRide?.destination?.lat,
    displayRide?.destination?.lng,
    driverLoc?.lat,
    driverLoc?.lng,
    mapReady,
    clearNearbyMarkers,
    showRouteLine,
    fetchLegRoute,
    followLeg,
  ]);

  useEffect(() => {
    const el = sheetRef.current;
    const screen = el?.closest('.rapido-active');
    if (!el || !screen) return undefined;
    const sync = () => {
      screen.style.setProperty('--active-sheet-h', `${Math.round(getSheetHeight())}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [sheetMinimized, displayRide?.status, getSheetHeight]);

  const statusRef = useRef(displayRide?.status);

  useEffect(() => {
    if (!mapReady || !displayRide?.status) return undefined;
    const statusChanged = statusRef.current !== displayRide.status;
    statusRef.current = displayRide.status;
    if (statusChanged || !initialFitDoneRef.current) {
      initialFitDoneRef.current = true;
      userPannedRef.current = false;
      setUserPanned(false);
      fitAllOnMap(true);
    }
    return undefined;
  }, [displayRide?.status, mapReady, fitAllOnMap]);

  useEffect(() => () => clearNearbyMarkers(), []);

  useEffect(() => {
    if (displayRide?.status !== 'searching') {
      setSearchTimedOut(false);
      return undefined;
    }
    if (displayRide?.searchExhausted) {
      setSearchTimedOut(true);
      return undefined;
    }
    const startedAtMs = searchCycleStartedAtMs(displayRide);
    // The optimistic ride object is shown before Firestore resolves its server
    // timestamp. Do not mistake that short gap for a 1970-era search.
    if (!startedAtMs) {
      setSearchTimedOut(false);
      return undefined;
    }
    const elapsedMs = Math.max(0, Date.now() - startedAtMs);
    if (elapsedMs >= SEARCH_TIMEOUT_MS) {
      setSearchTimedOut(true);
      return undefined;
    }
    setSearchTimedOut(false);
    const id = setTimeout(() => setSearchTimedOut(true), SEARCH_TIMEOUT_MS - elapsedMs);
    return () => clearTimeout(id);
  }, [
    displayRide?.status,
    displayRide?.searchExhausted,
    displayRide?.searchStartedAt,
    displayRide?.lastCancelledByDriverAt,
    displayRide?.createdAt,
    rideId,
  ]);

  // Single owner of both live ETAs. The server already computes a real ETA from
  // the same GPS ping this screen is subscribed to (maybePushLiveTripProgress
  // writes etaMin/etaPhase/etaAt onto rideLocations/{rideId} about every 20s),
  // using straight-line distance scaled by THIS ride's own booked road factor —
  // so it is routed-accurate without costing a Directions request. It is used
  // whenever it is for the leg on screen and recent; otherwise a local
  // straight-line estimate fills in, covering both the first seconds of a leg
  // and any stall in the relay.
  //
  // Deliberately no Directions call here. The map's route line is still drawn
  // from a real routed path once per status change (marker/route effect above);
  // repeating that call every 60s only to nudge the "x min" text was the single
  // largest recurring Maps cost in the app and added no accuracy the server
  // value doesn't already carry.
  useEffect(() => {
    const status = displayRide?.status;
    const serverFresh = Boolean(
      driverLoc
      && Number.isFinite(Number(driverLoc.etaMin))
      && Number(driverLoc.etaMin) >= 0
      && Number(driverLoc.etaAt) > 0
      && Date.now() - Number(driverLoc.etaAt) < ETA_SERVER_MAX_AGE_MS,
    );

    if (driverLoc && displayRide?.pickup?.lat != null
      && ['driver_assigned', 'arrived'].includes(status)) {
      setPickupEtaMin(
        serverFresh && driverLoc.etaPhase === 'pickup'
          ? Math.max(0, Math.round(Number(driverLoc.etaMin)))
          : Math.max(1, Math.ceil(
            distanceBetweenM(driverLoc, displayRide.pickup) / LOCAL_ETA_METRES_PER_MIN,
          )),
      );
    } else {
      setPickupEtaMin(null);
    }

    if (driverLoc && displayRide?.destination?.lat != null && status === 'started') {
      setDropEtaMin(
        serverFresh && driverLoc.etaPhase === 'drop'
          ? Math.max(0, Math.round(Number(driverLoc.etaMin)))
          : Math.max(1, Math.ceil(
            distanceBetweenM(driverLoc, displayRide.destination) / LOCAL_ETA_METRES_PER_MIN,
          )),
      );
    } else {
      setDropEtaMin(null);
    }
  }, [
    driverLoc,
    displayRide?.status,
    displayRide?.pickup?.lat,
    displayRide?.pickup?.lng,
    displayRide?.destination?.lat,
    displayRide?.destination?.lng,
  ]);

  useEffect(() => {
    setDismissedSearchActions(false);
  }, [rideId]);

  const pickupDistanceM = useMemo(() => {
    if (!driverLoc || !displayRide?.pickup) return null;
    return distanceBetweenM(driverLoc, displayRide.pickup);
  }, [driverLoc, displayRide?.pickup]);

  const stepTimestamps = useMemo(() => {
    const seen = stageSeenAtRef.current;
    // Server stage timestamp first, else the local time we first saw the stage.
    const stamp = (ts, stage) => formatTimeStr(ts) || formatTimeStr(seen[stage]);
    const isAssignedOrBeyond = ['driver_assigned', 'arrived', 'started', 'completed'].includes(displayRide?.status);
    return {
      searching: stamp(displayRide?.createdAt, 'searching'),
      driver_assigned: (isAssignedOrBeyond && displayRide?.driverId)
        ? stamp(displayRide?.assignedAt || displayRide?.acceptedAt, 'driver_assigned')
        : '',
      arrived: isAssignedOrBeyond ? stamp(displayRide?.arrivedAt, 'arrived') : '',
      started: isAssignedOrBeyond ? stamp(displayRide?.startedAt, 'started') : '',
      completed: stamp(displayRide?.completedAt, 'completed'),
    };
  }, [
    displayRide?.createdAt, displayRide?.assignedAt, displayRide?.acceptedAt,
    displayRide?.arrivedAt, displayRide?.startedAt, displayRide?.completedAt,
    displayRide?.status, displayRide?.driverId,
  ]);

  const captainArrivingSoon = displayRide?.status === 'driver_assigned' && (
    (pickupEtaMin != null && pickupEtaMin <= APP.captainArrivingEtaMin)
    || (pickupDistanceM != null && pickupDistanceM <= APP.captainArrivingDistanceM)
  );

  useEffect(() => {
    if (!captainArrivingSoon || notifiedArrivingRef.current) return;
    notifiedArrivingRef.current = true;
    const min = pickupEtaMin ?? Math.max(1, Math.ceil((pickupDistanceM || 400) / 250));
    toast(tRef.current('active.captainAboutToArrive'), {
      id: 'captain-arriving',
      icon: '🛺',
      duration: 6000,
    });
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([120, 60, 120]);
    }
  }, [captainArrivingSoon, pickupEtaMin, pickupDistanceM]);

  // Lock-screen live card for assigned / arrived / started.
  useEffect(() => {
    const status = displayRide?.status;
    if (!['driver_assigned', 'arrived', 'started'].includes(status)) {
      // AppContext owns tear-down; only clear here if there's truly no live ride
      // (leaving this to fire on every non-live render would fight AppContext).
      if (!status || ['completed', 'cancelled', 'searching'].includes(status)) clearLiveRideNotification();
      return undefined;
    }
    // The card only needs a refresh on a status change or a meaningful move —
    // not on every 3s driver tick (that was a native bridge call every 3s).
    // ~9s / 150m keeps the trip bar tracking elapsed time closely on short
    // Piler trips without going back to a per-tick bridge call.
    const statusChanged = liveCardThrottle.current.status !== status;
    liveCardThrottle.current.status = status;
    if (!statusChanged && !shouldRecompute(liveCardThrottle, driverLoc, 9000, 150)) return undefined;
    const vehicle = displayRide?.vehicleNumber || '';
    const pickupName = displayRide?.pickup?.name
      || displayRide?.pickup?.address
      || '';
    const destName = displayRide?.destination?.name
      || displayRide?.destination?.address
      || '';
    updateLiveRideNotification({
      rideId,
      status,
      vehicleNumber: vehicle,
      driverName: displayRide?.driverName,
      otp: tripOtp || displayRide?.otp,
      pickupName,
      destinationName: destName,
      // True trip progress: elapsed since startedAt vs. the live routed ETA to
      // the drop (falls back to the booking duration estimate).
      startedAtMs: tsMillis(displayRide?.startedAt) || null,
      durationMin: displayRide?.durationMin || null,
      etaMin: status === 'driver_assigned' ? pickupEtaMin : (status === 'started' ? dropEtaMin : null),
    });
    return undefined;
  }, [
    rideId,
    displayRide?.status,
    displayRide?.vehicleNumber,
    displayRide?.driverName,
    tripOtp,
    displayRide?.otp,
    displayRide?.pickup?.name,
    displayRide?.pickup?.address,
    displayRide?.pickup?.lat,
    displayRide?.pickup?.lng,
    displayRide?.destination?.name,
    displayRide?.destination?.address,
    displayRide?.destination?.lat,
    displayRide?.destination?.lng,
    driverLoc,
    pickupEtaMin,
    dropEtaMin,
  ]);

  useEffect(() => {
    if (displayRide?.status !== 'driver_assigned') {
      notifiedArrivingRef.current = false;
    }
    if (displayRide?.status === 'searching') {
      notifiedAcceptRef.current = false;
      notifiedArrivedRef.current = false;
    }
  }, [displayRide?.status, rideId]);

  const driverLocActive = Boolean(
    rideId && displayRide?.driverId
    && ['driver_assigned', 'arrived', 'started'].includes(displayRide.status),
  );

  // Captain-identity self-heal. The Firestore listener can stall in the WebView
  // for a whole trip; RTDB then advances status but cannot carry driverName /
  // vehicle / arrivedAt / startedAt (identity is Firestore-only by design). If
  // we are in an active status without a captain name, force STRICT server
  // reads (the local cache is the stale copy in this failure) every few
  // seconds until the identity lands or the trip ends. No-op on a healthy ride.
  useEffect(() => {
    const active = ['driver_assigned', 'arrived', 'started'].includes(displayRide?.status);
    if (!rideId || !active || displayRide?.driverName) return undefined;
    let stop = false;
    let tries = 0;
    const MAX_TRIES = 8;
    const RETRY_MS = 3000;
    const heal = async () => {
      if (stop || tries >= MAX_TRIES || document.visibilityState !== 'visible') return;
      tries += 1;
      try {
        const fresh = await getRideFromServerStrict(rideId);
        if (stop) return;
        if (fresh) applyRide(fresh);
        if (fresh?.driverName) return;
      } catch { /* stalled — keep retrying */ }
      if (!stop) window.setTimeout(heal, RETRY_MS);
    };
    heal();
    return () => { stop = true; };
  }, [rideId, displayRide?.status, displayRide?.driverName, applyRide]);

  useEffect(() => {
    if (!driverLocActive) {
      setDriverLoc(null);
      return undefined;
    }
    // Only accept a fix that's at least as fresh as what we already show, so the
    // realtime listener and the poll below don't fight each other.
    const applyLoc = (loc) => {
      if (!loc || loc.lat == null) return;
      // Record every time this channel actually delivers something, regardless
      // of whether it changed displayed state — the poll below uses this to
      // tell "realtime is alive" apart from "Anna just hasn't moved much".
      realtimeLocAtRef.current = Date.now();
      setDriverLoc((prev) => (
        !prev || (Number(loc.updatedAt) || 0) >= (Number(prev.updatedAt) || 0) ? loc : prev
      ));
    };
    return listenToRideDriverLocation(rideId, applyLoc);
  }, [driverLocActive, rideId]);

  // Staleness backstop: if driverLoc simply never gets a fresh write again (the
  // Anna's tracking stopped for any reason — app closed, GPS lost, etc.), the
  // listener above goes quiet and nothing else here would ever notice on its
  // own. Check on an independent timer, not just when new data arrives, so a
  // feed that's stopped entirely still gets caught instead of leaving the map
  // frozen with no explanation.
  useEffect(() => {
    if (!driverLocActive) {
      setLocationStale(false);
      return undefined;
    }
    const maxAgeMs = (APP.captainLocationMaxAgeSec || 180) * 1000;
    const check = () => {
      const updatedAt = Number(driverLoc?.updatedAt) || 0;
      setLocationStale(Boolean(updatedAt && Date.now() - updatedAt > maxAgeMs));
    };
    check();
    const id = setInterval(check, 15000);
    return () => clearInterval(id);
  }, [driverLocActive, driverLoc?.updatedAt]);

  // Fallback: the rideLocations WebSocket stalls silently in the Android WebView
  // and there's no auto-recovery — so this backstops it. Only actually reads
  // once the realtime channel above has gone quiet for a while, instead of
  // reading on every tick regardless — same recovery guarantee (still checked
  // often, still forced on foreground/reconnect, since those are exactly the
  // moments a stall is most likely), far fewer reads while realtime is healthy
  // (the common case).
  useEffect(() => {
    if (!driverLocActive) return undefined;
    const REALTIME_SILENT_MS = 9000;
    let stopped = false;
    const pull = async (force = false) => {
      if (stopped || document.visibilityState !== 'visible') return;
      if (!force && Date.now() - realtimeLocAtRef.current < REALTIME_SILENT_MS) return;
      const loc = await getRideDriverLocationOnce(rideId);
      if (stopped || !loc || loc.lat == null) return;
      setDriverLoc((prev) => (
        !prev || (Number(loc.updatedAt) || 0) >= (Number(prev.updatedAt) || 0) ? loc : prev
      ));
    };
    const onInterval = () => pull(false);
    const onForce = () => pull(true);
    pull(true);
    const id = setInterval(onInterval, 3000);
    const onVisible = () => { if (document.visibilityState === 'visible') onForce(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onForce);
    window.addEventListener('online', onForce);
    return () => {
      stopped = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onForce);
      window.removeEventListener('online', onForce);
    };
  }, [driverLocActive, rideId]);

  useEffect(() => {
    if (displayRide?.status !== 'searching') {
      setSearchElapsedSec(0);
      return undefined;
    }
    const updateElapsed = () => {
      const startedAtMs = searchCycleStartedAtMs(displayRide);
      setSearchElapsedSec(startedAtMs
        ? Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
        : 0);
    };
    updateElapsed();
    const elapsedId = setInterval(updateElapsed, 1000);
    return () => {
      clearInterval(elapsedId);
    };
  }, [
    displayRide?.status,
    displayRide?.searchStartedAt,
    displayRide?.lastCancelledByDriverAt,
    displayRide?.createdAt,
    rideId,
  ]);

  const formatSearchElapsed = (sec) => {
    const sVal = Math.max(0, Number(sec) || 0);
    const m = Math.floor(sVal / 60);
    const s = sVal % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleCancel = async (reason) => {
    if (!online) {
      toast.error(t('network.offlineBody') || 'Reconnect before changing your ride.');
      return;
    }
    setCancelling(true);
    try {
      await cancelRide(rideId, reason);
      setShowCancel(false);
      try { sessionStorage.removeItem('mtl_rides_pickup_v1'); } catch { /* */ }
      navigate('/', { replace: true });
    } catch {
      toast.error(t('book.bookError'));
    } finally { setCancelling(false); }
  };

  const handleRating = async ({ rating, tip, comment, tags }) => {
    if (!online) {
      toast.error(t('network.offlineBody') || 'Reconnect before submitting feedback.');
      return;
    }
    setRatingBusy(true);
    try {
      await submitRideRating(rideId, { rating, tip, comment, tags });
      toast.dismiss('status-completed');
      toast.success(t('rating.thanks'), { id: 'feedback-toast' });
      setShowRating(false);
      queueReviewAfterCompletedRide();
      try { sessionStorage.removeItem('mtl_rides_pickup_v1'); } catch { /* */ }
      navigate('/', { replace: true });
    } catch {
      toast.error(t('authErrors.default'));
    } finally {
      setRatingBusy(false);
    }
  };

  // Store the likely post-completion count while the user document still
  // contains the pre-completion aggregate. If the backend aggregate has
  // already arrived, accepting the current milestone handles that case too.
  // No rating value, comment, or rating-sheet choice participates here.
  const queueReviewAfterCompletedRide = () => {
    const rawCurrent = userProfile?.totalCompletedRides;
    const current = Number(rawCurrent);
    const candidates = [current, current + 1];
    const target = candidates.find((count) => Number.isFinite(count) && [3, 10].includes(Math.floor(count)));
    // Once the authoritative aggregate exists, non-milestone rides do not
    // need any follow-up read at all.
    if (rawCurrent != null && !target) return;
    queuePlayReviewForCompletedRide({
      uid: user?.uid,
      rideId,
      targetCompletedRides: target,
    });
  };

  const openCancel = () => {
    setShowChat(false);
    setShowSos(false);
    setShowRating(false);
    setShowCancel(true);
  };

  const openChat = () => {
    setShowCancel(false);
    setShowSos(false);
    setShowChat(true);
  };

  const openSos = () => {
    setShowChat(false);
    setShowCancel(false);
    setShowSos(true);
  };

  const openRating = () => {
    setShowChat(false);
    setShowCancel(false);
    setShowSos(false);
    setShowRating(true);
  };

  const handleCall = () => {
    const raw = displayRide?.driverPhone || driverProfile?.phone;
    if (!raw) return;
    const phone = String(raw).replace(/\D/g, '').slice(-10);
    if (phone.length < 10) return;
    logRideEvent(rideId, 'driver_called', { rideStatus: displayRide?.status });
    window.location.href = `tel:+91${phone}`;
  };

  // Prefetch share token so Share opens WhatsApp instantly.
  useEffect(() => {
    if (!rideId || !displayRide) return;
    if (!['driver_assigned', 'arrived', 'started'].includes(displayRide.status)) return;
    if (displayRide.shareToken) return;
    let cancelled = false;
    ensureRideShare(rideId)
      .then((ensured) => {
        if (cancelled || !ensured?.shareToken) return;
        setRide((prev) => (prev ? {
          ...prev,
          shareToken: ensured.shareToken,
          vehicleNumber: prev.vehicleNumber || ensured.vehicleNumber || prev.vehicleNumber,
        } : prev));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [rideId, displayRide?.status, displayRide?.shareToken]);

  const handleShareTrip = async () => {
    if (!displayRide) return;
    const token = displayRide.shareToken;
    const link = trackUrlForToken(token);
    if (!link) {
      toast(t('active.sharePreparing'), { icon: '🔗', duration: 2500 });
      ensureRideShare(rideId)
        .then((ensured) => {
          if (ensured?.shareToken) {
            setRide((prev) => (prev ? {
              ...prev,
              shareToken: ensured.shareToken,
              vehicleNumber: prev.vehicleNumber || ensured.vehicleNumber || prev.vehicleNumber,
            } : prev));
            toast.success(t('active.shareReady'), { duration: 2000 });
          } else {
            toast.error(t('active.shareUnavailable'));
          }
        })
        .catch(() => toast.error(t('active.shareUnavailable')));
      return;
    }
    const dest = displayRide.destination?.name || '';
    const status = t(`status.${displayRide.status}`);
    let vehicle = displayRide.vehicleNumber
      || driverProfile?.vehicleNumber
      || '';
    // Ride doc's copy can be empty if it was blank on the driver's profile at
    // accept time — drivers/{uid} isn't readable by the passenger directly, so
    // re-resolve it server-side via ensureRideShare (same helper the public
    // track page already uses) in case the plate was added/fixed since.
    if (!vehicle && displayRide.driverId) {
      try {
        const ensured = await ensureRideShare(rideId);
        vehicle = ensured?.vehicleNumber || '';
        if (vehicle) {
          setRide((prev) => (prev ? { ...prev, vehicleNumber: vehicle } : prev));
        }
      } catch { /* best effort — fall back to '—' below */ }
    }
    const msg = t('active.shareTripMsg', {
      status,
      dest,
      fare: displayRide.fare,
      name: formatDriverName(displayRide.driverName),
      vehicle: vehicle || '—',
      link,
    });
    openWhatsappShare(msg);
    logRideEvent(rideId, 'trip_shared', { via: 'whatsapp', rideStatus: displayRide?.status });
  };

  const handleKeepWaiting = async () => {
    logRideEvent(rideId, 'kept_waiting', { searchElapsedSec, searchRing: displayRide?.dispatchRing || 1 });
    setDismissedSearchActions(true);
    setSearchTimedOut(false);
    hapticSelection();
    toast.success(t('active.keepWaitingNotifying') || 'Looking for nearby Annas again…', { id: 'keep-waiting' });
    try {
      await keepWaitingRide(rideId);
    } catch (err) {
      console.warn('keepWaitingRide failed:', err?.message || err);
    }
  };

  // Cancels and opens Book with the drop kept, so the rider only re-picks the
  // pickup. Used by "No Anna found → book again" and the cancel flow's
  // "Wrong pickup → cancel and set a new pickup" (free: still searching).
  const handleBookAgain = async (reason = 'no_anna_book_again') => {
    setCancelling(true);
    try {
      await cancelRide(rideId, reason);
      navigate('/', {
        replace: true,
        state: {
          rebookDrop: displayRide?.destination?.lat ? displayRide.destination : undefined,
        },
      });
    } catch {
      toast.error(t('book.bookError'));
    } finally {
      setCancelling(false);
    }
  };

  const handleCallSupport = () => {
    const tel = buildTelUrl();
    if (tel === '#') {
      toast.error(t('active.sosUnavailable'));
      return;
    }
    logRideEvent(rideId, 'support_called', { rideStatus: displayRide?.status });
    window.location.href = tel;
  };

  const handleVehicleMatch = async (matched) => {
    if (!online) {
      toast.error(t('network.offlineBody') || 'Reconnect before sending this report.');
      return;
    }
    if (!rideId || matchBusy || displayRide?.vehicleMatch != null) return;
    setMatchBusy(true);
    try {
      await reportVehicleMatch(
        rideId,
        matched,
        displayRide?.vehicleNumber || driverProfile?.vehicleNumber || '',
      );
      toast.success(matched ? t('active.matchThanksYes') : t('active.matchThanksNo'));
    } catch {
      toast.error(t('authErrors.default'));
    } finally {
      setMatchBusy(false);
    }
  };

  const boostTier = resolveBoostTier(displayRide?.nearestDriverKm);

  const handleBoostFare = async (amount) => {
    if (!online) {
      toast.error(t('network.offlineBody') || 'Reconnect before updating your fare.');
      return;
    }
    if (boostingAmt !== null || !displayRide?.id || displayRide.status !== 'searching') return;
    const currentTip = Number(displayRide.tip || displayRide.fareTip || 0);
    if (currentTip + amount > boostTier.cap) {
      toast.error(t('active.boostMax', { cap: boostTier.cap }));
      return;
    }
    setBoostingAmt(amount);
    hapticSelection();
    try {
      await boostRideFare(displayRide.id, amount);
      hapticSuccess();
      toast.success(t('active.boostSuccess', { amount }));
    } catch (err) {
      // Never show the raw server string — map it to a translated message.
      const mapped = boostErrorKey(err);
      toast.error(mapped ? t(mapped.key, mapped.vars) : t('active.boostFailed'));
    } finally {
      setBoostingAmt(null);
    }
  };

  const handleSos = () => {
    logRideEvent(rideId, 'sos_opened', { rideStatus: displayRide?.status });
    openSos();
  };

  // The fixes the cancel flow may offer instead of cancelling. A fix that is
  // not possible right now is left out, so the flow never shows a dead button.
  const cancelFlowActions = (() => {
    const status = displayRide?.status;
    const searching = status === 'searching';
    const assigned = status === 'driver_assigned' || status === 'arrived';
    const currentTip = Number(displayRide?.tip || displayRide?.fareTip || 0);
    const boostAmount = searching && online && !displayRide?.noCaptainsOnline
      ? boostTier.amounts.find((amt) => currentTip + amt <= boostTier.cap)
      : undefined;
    const annaPhone = String(displayRide?.driverPhone || driverProfile?.phone || '').replace(/\D/g, '');
    return {
      boost: boostAmount ? {
        amount: boostAmount,
        fare: Number(displayRide?.fare || 0) + boostAmount,
        run: async () => { setShowCancel(false); await handleBoostFare(boostAmount); },
      } : null,
      call: assigned && annaPhone.length >= 10 ? () => { setShowCancel(false); handleCall(); } : null,
      chat: assigned ? () => openChat() : null,
      rebook: searching ? () => handleBookAgain('wrong_pickup') : null,
      support: () => { setShowCancel(false); handleCallSupport(); },
    };
  })();

  if (rideMissing && rideLoadTried) {
    return (
      <div className="rapido-screen rapido-loading-screen">
        <div className="rapido-loading-auto">🛺</div>
        <p style={{ fontWeight: 700, marginTop: 12 }}>{t('active.rideNotFound')}</p>
        <p className="text-muted">{t('active.rideNotFoundSub')}</p>
        <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => navigate('/', { replace: true })}>
          {t('nav.home')}
        </button>
      </div>
    );
  }

  if (!displayRide) {
    return (
      <div className="rapido-screen rapido-active">
        <div ref={mapRef} className="rapido-map" />
        <div className="rapido-booking-radar-overlay">
          <div className="rapido-booking-radar-pulse" />
          <div className="rapido-booking-radar-pulse delay" />
          <div className="rapido-booking-radar-card">
            <div className="rapido-booking-radar-icon-wrap">
              <span className="rapido-booking-radar-auto">🛺</span>
            </div>
            <h3 className="rapido-booking-radar-title">{t('active.findingCaptain') || 'Finding nearby autos...'}</h3>
            <p className="rapido-booking-radar-sub">{t('active.ringNear') || 'Connecting to nearby autos...'}</p>
            <div className="rapido-booking-radar-dots">
              <span /><span /><span />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const canCancel = ['searching', 'driver_assigned', 'arrived'].includes(displayRide.status);
  // Don't make a rider wait 40 s for the tip option when the server already
  // knows the nearest auto is far (or nobody is online). `nearestDriverKm` is
  // written by dispatch; a missing value must NOT read as 0 km (Number(null)=0).
  const searchedFarOrEmpty = displayRide.noCaptainsOnline === true
    || (displayRide.nearestDriverKm != null && Number(displayRide.nearestDriverKm) >= 1.5);
  const headline = t(PHASE_HEADLINE[displayRide.status] || 'active.findingCaptain');
  const searchSub = (() => {
    if (displayRide.status !== 'searching') return '';
    if (displayRide.searchExhausted || searchTimedOut) {
      return displayRide.searchExhausted ? t('active.noCaptain') : t('active.searchTimeout');
    }
    const ring = Number(displayRide.dispatchRing) || 1;
    if (ring >= 4) return t('active.ringArea', { city: APP.city });
    if (ring >= 3) return t('active.ringWider');
    // `nearbyDrivers` is every online auto within the map radius — not the
    // (smaller) set the current dispatch round has alerted — so only say how
    // many are nearby, never that they are all being notified.
    if (nearbyDrivers.length > 0) {
      return nearbyDrivers.length === 1
        ? t('active.notifyingNearbyOne')
        : t('active.notifyingNearby', { count: nearbyDrivers.length });
    }
    if (ring >= 2) return t('active.ringWider');
    return t('active.ringNear');
  })();
  return (
    <div className="rapido-screen rapido-active">
      <div ref={mapRef} className="rapido-map" />

      <button
        type="button"
        className="rapido-back-fab"
        onClick={() => {
          // While the ride is live there is nowhere to go back to — Home just
          // bounces straight back here and reloads the map. Background the app,
          // matching the hardware back button. Terminal states go Home normally.
          const live = ['searching', 'driver_assigned', 'arrived', 'started'].includes(displayRide?.status);
          if (live && isNativeApp()) minimizeApp();
          else navigate('/', { replace: true });
        }}
        aria-label={t('nav.home')}
      >
        ←
      </button>

      {['driver_assigned', 'arrived', 'started'].includes(displayRide.status) && (
        <div className="rapido-active-fabs">
          <button
            type="button"
            className="rapido-share-fab icon-only"
            onClick={handleShareTrip}
            aria-label={t('active.share')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
              <circle cx="18" cy="5" r="2.4" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="6" cy="12" r="2.4" stroke="currentColor" strokeWidth="1.8" />
              <circle cx="18" cy="19" r="2.4" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.2 10.8l7.5-4.2M8.2 13.2l7.5 4.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
          <button type="button" className="rapido-sos-fab" onClick={handleSos} aria-label={t('active.sos')}>
            <span aria-hidden>SOS</span>
          </button>
        </div>
      )}

      {userPanned && (
        <button
          type="button"
          className="rapido-recenter-fab"
          onClick={() => {
            userPannedRef.current = false;
            setUserPanned(false);
            fitAllOnMap(true);
          }}
          aria-label={t('active.recenterMap')}
        >
          <MapRecenterIcon />
        </button>
      )}

      {displayRide?.status === 'searching' && (
        <div className="searching-floating-pill">
          <span className="searching-floating-dot" />
          <span>{t('active.lookingNearbyShort')}</span>
          <span className="searching-floating-timer">· {formatSearchElapsed(searchElapsedSec)}</span>
        </div>
      )}

      {showNightShield && ['driver_assigned', 'arrived', 'started'].includes(displayRide?.status) && (
        <div className="night-shield-toast" role="alert">
          <div className="night-shield-icon">🌙</div>
          <div className="night-shield-content">
            <div className="night-shield-title">{t('active.nightShieldTitle')}</div>
            <div className="night-shield-text">
              {t('active.nightShieldBody', { city: APP.city })}
            </div>
            <div className="night-shield-actions">
              <button
                type="button"
                className="night-shield-btn-share"
                onClick={() => {
                  setShowNightShield(false);
                  handleShareTrip();
                }}
              >
                📲 {t('active.nightShieldShare')}
              </button>
              <button
                type="button"
                className="night-shield-btn-dismiss"
                onClick={() => setShowNightShield(false)}
              >
                ✕ {t('active.nightShieldOk')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        ref={sheetRef}
        className={`rapido-sheet open rapido-active-sheet${sheetMinimized ? ' collapsed' : ''}`}
      >
        <button
          type="button"
          className="rapido-sheet-handle"
          onClick={() => setSheetMinimized((v) => !v)}
          aria-label={sheetMinimized ? t('active.showDetails') : t('active.showMap')}
        />

        {sheetMinimized ? (
          <button
            type="button"
            className="rapido-active-peek"
            onClick={() => setSheetMinimized(false)}
          >
            <span className={`status-pill status-${displayRide.status}`}>
              {t(`status.${displayRide.status}`)}
            </span>
            <span className="rapido-active-peek-title">
              {displayRide.status === 'searching'
                ? t('active.findingCaptain')
                : headline}
            </span>
            <span className="rapido-active-peek-action">{t('active.showDetails')} ↑</span>
          </button>
        ) : (
          <div className="rapido-active-sheet-body">
            <RideProgress
              status={displayRide.status}
              t={t}
              timestamps={stepTimestamps}
            />

            {displayRide.status === 'searching' ? (
              <div className="rapido-active-status">
                <span className={`status-pill status-${displayRide.status}`}>{t(`status.${displayRide.status}`)}</span>
                <h2>{headline}{displayRide.status === 'searching' && <span className="searching-ellipsis" aria-hidden="true">...</span>}</h2>
                <p className="rapido-nearby-sub">{searchSub}</p>
              </div>
            ) : (
              <>
                {/* Mint ETA Banner Card */}
                {['driver_assigned', 'arrived', 'started'].includes(displayRide.status) && (
                  <div className="active-eta-banner-card">
                    <div className="eta-banner-icon-circle">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className="eta-banner-text">
                      <strong className="eta-banner-title">
                        {displayRide.status === 'driver_assigned' && t('active.etaArriving', { min: pickupEtaMin || 1 })}
                        {displayRide.status === 'arrived' && t('active.etaArrived')}
                        {displayRide.status === 'started' && (dropEtaMin ? t('active.etaHeadingMin', { min: dropEtaMin }) : t('active.etaHeading'))}
                      </strong>
                      <span className="eta-banner-sub">
                        {displayRide.status === 'driver_assigned' && t('active.etaSubAssigned')}
                        {displayRide.status === 'arrived' && t('active.etaSubArrived')}
                        {displayRide.status === 'started' && t('active.etaSubStarted')}
                      </span>
                    </div>
                    <div className="eta-banner-auto-art" aria-hidden>
                      <svg width="40" height="34" viewBox="0 0 100 100" fill="none" stroke="#00875a" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M88 15H18C10 15 4 21 4 29V66C4 70 7 73 11 73H13" />
                        <path d="M29 73H72" />
                        <path d="M34 18V36H6" />
                        <path d="M6 46H34" />
                        <path d="M38 18V46C38 52 42 56 48 56H64V18" />
                        <path d="M68 18V44H84L78 18H68Z" fill="#ffcc00" fillOpacity="0.2" />
                        <path d="M78 18L86 48V58H68" />
                        <path d="M86 58L97 68H78L86 58Z" />
                        <circle cx="21" cy="76" r="9" strokeWidth="7" fill="#334155" />
                        <circle cx="87" cy="78" r="9" strokeWidth="7" fill="#334155" />
                      </svg>
                    </div>
                  </div>
                )}
                {/* Pickup waiting clock — renders nothing unless the server accepted the arrival as proof. */}
                <WaitingClock ride={displayRide} t={t} />
                {/* 10/10 Driver & OTP Card */}
                {displayRide.driverName && (
                  <CaptainHeaderCard
                    ride={displayRide}
                    driver={driverProfile}
                    otp={tripOtp || displayRide.otp || getCachedRideOtp(rideId)}
                    t={t}
                    showOtp={['driver_assigned', 'arrived'].includes(displayRide.status)}
                    showActions={['driver_assigned', 'arrived'].includes(displayRide.status)}
                    onCall={handleCall}
                    onChat={openChat}
                    onViewProfile={() => setShowDriverModal(true)}
                  />
                )}

                {riderNote && displayRide.driverId && ['driver_assigned', 'arrived'].includes(displayRide.status) && (
                  <div className="rider-note-sent" role="status">
                    <strong>📝 {t('riderNote.yourNote')}</strong>
                    <span className="rider-note-sent-text">{riderNoteSummary(riderNote, t)}</span>
                    <span className={`rider-note-sent-state${riderNoteSeen(riderNote, displayRide) ? ' seen' : ''}`}>
                      {riderNoteSeen(riderNote, displayRide) ? `✓ ${t('riderNote.seen')}` : t('riderNote.notSeenYet')}
                    </span>
                  </div>
                )}

                {/* Degraded card: Firestore identity hasn't landed yet (WebView
                    stall). RTDB confirmed a driver so the trip is real — show a
                    calm placeholder instead of a blank gap. Layer 2's retry
                    swaps in the real card as soon as the read succeeds. */}
                {!displayRide.driverName
                  && displayRide.driverId
                  && ['driver_assigned', 'arrived', 'started'].includes(displayRide.status) && (
                  <div className="captain-header-card">
                    <div className="captain-header-main">
                      <span className="captain-header-avatar" aria-hidden>🛺</span>
                      <div>
                        <div className="captain-header-name">{t('active.captainConnecting')}</div>
                        <div className="captain-header-meta">
                          <span style={{ color: 'var(--muted, #667085)', fontWeight: 600 }}>
                            {t('active.captainConnectingSub')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {displayRide.status === 'completed' && (
                  <div className="completed-card-wrap">
                    <div className="completed-fare-banner">
                      <span className="completed-badge">✓ {t('active.completed')}</span>
                      <span className="completed-fare-text">{t('active.cashPaid', { fare: displayRide.fare })}</span>
                    </div>

                    {displayRide.rating && (
                      <div className="inline-rated-badge">
                        <span className="green-check">✓</span> {t('active.ratingSubmitted', { stars: displayRide.rating })}
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary done-btn"
                      onClick={() => navigate('/', { replace: true })}
                    >
                      {t('active.bookAnother')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost done-btn"
                      onClick={() => navigate(`/rides/${rideId}`)}
                    >
                      {t('active.viewReceipt') || 'View receipt'}
                    </button>
                  </div>
                )}

                {displayRide.status === 'arrived'
                  && displayRide.vehicleNumber
                  && displayRide.vehicleMatch == null && (
                  <div className="vehicle-match-card" role="group" aria-label={t('active.matchAsk')}>
                    <p className="vehicle-match-ask">
                      {t('active.matchAsk', { no: displayRide.vehicleNumber })}
                    </p>
                    <div className="vehicle-match-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={matchBusy || !online}
                        onClick={() => handleVehicleMatch(true)}
                      >
                        {t('active.matchYes')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        disabled={matchBusy || !online}
                        onClick={() => handleVehicleMatch(false)}
                      >
                        {t('active.matchNo')}
                      </button>
                    </div>
                  </div>
                )}

                {displayRide.status === 'arrived' && displayRide.vehicleMatch != null && (
                  <p className="vehicle-match-recorded text-muted">
                    {displayRide.vehicleMatch ? t('active.matchRecordedYes') : t('active.matchRecordedNo')}
                  </p>
                )}

                {locationStale && !unexpectedStop && (
                  <p className="location-stale-notice text-muted">
                    {t('active.locationStale')}
                  </p>
                )}

                {unexpectedStop && (
                  <div className="unexpected-stop-banner" role="alert">
                    <div className="stop-banner-icon">⚠️</div>
                    <div className="stop-banner-body">
                      <strong>{t('active.unexpectedStopTitle')}</strong>
                      <p>{t('active.unexpectedStopSub')}</p>
                      <div className="stop-banner-actions">
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => {
                          logRideEvent(rideId, 'passenger_safe', { idleMinutes: 5 });
                          setUnexpectedStop(false);
                        }}>
                          {t('active.imSafe')}
                        </button>
                        <button type="button" className="btn btn-sm btn-outline" onClick={() => {
                          const tel = buildTelUrl();
                          if (tel !== '#') {
                            logRideEvent(rideId, 'support_called', { rideStatus: 'started', trigger: 'idle_stop' });
                            window.location.href = tel;
                          }
                        }}>
                          📞 {t('sos.callSupport')}
                        </button>
                        <button type="button" className="btn btn-sm btn-danger" onClick={handleSos}>
                          🚨 {t('active.sos')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* 10/10 Route & Fare Card */}
                <TripFareDetails ride={displayRide} t={t} />

                {/* 10/10 Safety Share Card */}
                {['driver_assigned', 'arrived', 'started'].includes(displayRide.status) && (
                  <div className="active-safety-share-card">
                    <div className="safety-share-left">
                      <div className="safety-share-icon">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00875a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                          <polyline points="9 12 11 14 15 10"/>
                        </svg>
                      </div>
                      <div className="safety-share-text">
                        <span className="safety-share-title">{t('active.safetyFirst')}</span>
                        <span className="safety-share-sub">{t('active.safetyShareSub')}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="safety-share-btn"
                      onClick={handleShareTrip}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                        <polyline points="16 6 12 2 8 6"/>
                        <line x1="12" y1="2" x2="12" y2="15"/>
                      </svg>
                      <span>{t('active.shareLiveTrip')}</span>
                    </button>
                  </div>
                )}

                {/* 10/10 Cancel Ride Row */}
                {canCancel && (
                  <button
                    type="button"
                    className="active-cancel-ride-row"
                    disabled={cancelling}
                    onClick={openCancel}
                  >
                    <div className="cancel-row-left">
                      <div className="cancel-icon-circle">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </div>
                      <div className="cancel-text-col">
                        <span className="cancel-title">{t('active.cancel')}</span>
                        <span className="cancel-sub">{t('active.cancelChargesMayApply')}</span>
                      </div>
                    </div>
                    <span className="cancel-chevron" aria-hidden>›</span>
                  </button>
                )}
              </>
            )}

            {displayRide.status === 'searching' && (searchElapsedSec >= 40 || searchedFarOrEmpty || Number(displayRide.tip || displayRide.fareTip || 0) > 0) && (
              <div className="rapido-boost-container">
                <div className="rapido-boost-header">
                  <span className="rapido-boost-title">{t('active.boostTitle')}</span>
                  {Number(displayRide.tip || displayRide.fareTip || 0) > 0 && (
                    <span className="rapido-boost-applied">
                      {t('active.boostApplied', { tip: displayRide.tip || displayRide.fareTip, fare: displayRide.fare })}
                    </span>
                  )}
                </div>
                {Number(displayRide.tip || displayRide.fareTip || 0) === 0 && (
                  <p className="rapido-boost-hint">
                    {displayRide.noCaptainsOnline
                      ? t('active.boostNoCaptains')
                      : Number.isFinite(Number(displayRide.nearestDriverKm))
                        ? t('active.boostHintDistance', { km: Number(displayRide.nearestDriverKm).toFixed(1) })
                        : t('active.boostHint')}
                  </p>
                )}
                {!displayRide.noCaptainsOnline && (
                  <div className="rapido-boost-chips">
                    {boostTier.amounts.map((amt) => {
                      const currentTip = Number(displayRide.tip || displayRide.fareTip || 0);
                      const disabled = !online || boostingAmt !== null || currentTip + amt > boostTier.cap;
                      return (
                        <button
                          key={amt}
                          type="button"
                          className={`rapido-boost-chip ${currentTip > 0 ? 'active' : ''}`}
                          disabled={disabled}
                          onClick={() => handleBoostFare(amt)}
                        >
                          {boostingAmt === amt ? '⏳...' : `+₹${amt}`}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {displayRide.status === 'searching' && (
              <TripFareDetails ride={displayRide} t={t} />
            )}

            {displayRide.status === 'searching' && canCancel && (
              <>
                {searchTimedOut && !dismissedSearchActions && (
                  <div className="rapido-search-next">
                    <p className="rapido-search-timeout-hint">
                      {displayRide.searchExhausted
                        ? (!displayRide.noCaptainsOnline && Number.isFinite(Number(displayRide.nearestDriverKm))
                          ? t('active.noCaptainHintDistance', { km: Number(displayRide.nearestDriverKm).toFixed(1) })
                          : t('active.noCaptainHint'))
                        : t('active.searchTimeoutHint')}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={cancelling || !online}
                      onClick={handleKeepWaiting}
                    >
                      {t('active.keepWaiting')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline"
                      disabled={cancelling || !online}
                      onClick={() => handleBookAgain()}
                    >
                      {t('active.bookAgain')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={cancelling}
                      onClick={handleCallSupport}
                    >
                      {t('active.callSupport')}
                    </button>
                  </div>
                )}
                <button type="button" className="rapido-cancel-btn rapido-cancel-quiet" disabled={cancelling} onClick={openCancel}>
                  {t('active.cancel')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {showCancel && (
        <CancelReasonModal
          t={t}
          busy={cancelling || boostingAmt !== null}
          onClose={() => setShowCancel(false)}
          onConfirm={handleCancel}
          stage={cancelStage(displayRide?.status)}
          acceptedAt={displayRide?.acceptedAt || displayRide?.assignedAt}
          lateCount={Number(userProfile?.lateCancellationsCount) || 0}
          annaName={formatDriverName(displayRide?.driverName)}
          actions={cancelFlowActions}
        />
      )}

      {showRating && (
        <RatingSheet
          t={t}
          fare={displayRide.fare}
          busy={ratingBusy}
          onSubmit={handleRating}
          onSkip={() => {
            setShowRating(false);
            queueReviewAfterCompletedRide();
            navigate('/rides', { replace: true });
          }}
        />
      )}

      {showSos && (
        <SosSheet
          t={t}
          rideId={rideId}
          onClose={() => setShowSos(false)}
        />
      )}

      {showDriverModal && (
        <DriverProfileModal
          driver={driverProfile}
          ride={displayRide}
          t={t}
          onClose={() => setShowDriverModal(false)}
        />
      )}

      <ChatPanel
        open={showChat}
        onClose={() => setShowChat(false)}
        rideId={rideId}
        userId={user?.uid}
        role="customer"
        t={t}
      />
    </div>
  );
}
